'use client';

import { ArrowRight } from '@byway/icons';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { DialogTrigger, Button as RACButton } from 'react-aria-components';
import { AnimatedPrice } from '~/components/AnimatedPrice';
import { Button } from '~/components/Button';
import { OverviewTabNav } from '~/components/CheckoutSections/Overview/OverviewTabNav';
import { Link } from '~/components/Link';
import { Popover } from '~/components/Popover';
import type { ScrollspyNavItem } from '~/components/ScrollspyNav';
import { TripLoyaltyPriceRow } from '~/components/TripOverview/booking/TripLoyaltyPriceRow';
import { TripPriceDetailsContent } from '~/components/TripOverview/booking/TripPriceDetailsContent';
import { TripPriceSkeleton } from '~/components/TripOverview/booking/TripPriceSkeleton';
import { SaveTripButton } from '~/components/TripOverview/hero/SaveTripButton';
import { ShareTripButton } from '~/components/TripOverview/hero/ShareTripButton';
import { useTripOverviewSummary } from '~/components/TripOverview/hooks/useTripOverviewSummary';
import { useTripPriceSummary } from '~/components/TripOverview/hooks/useTripPriceSummary';
import { useIsTripUpdating } from '~/hooks/useIsTripUpdating';
import { getPathname } from '~/i18n/navigation';
import { useTripBookingSelection } from '~/stores/tpv2/tripBookingStore';
import { trackFunnelEvent } from '~/utils/funnelTracking';
import { pricePerPerson as pricePerPersonForPax } from '~/utils/paxHelpers';
import { scrollIntoViewSettled } from '~/utils/scrollIntoViewSettled';
import styles from './ItineraryStickyBar.module.css';

const BAR_HEIGHT = 149;
const SECTION_ACTIVATION_OFFSET = BAR_HEIGHT + 300;

type ItineraryStickyBarProps = {
  nextAccommodationLabel: string;
  totalPerPersonLabel: string;
  overviewTabs: readonly ScrollspyNavItem[];
};

export const ItineraryStickyBar = ({
  nextAccommodationLabel,
  totalPerPersonLabel,
  overviewTabs
}: Readonly<ItineraryStickyBarProps>) => {
  const barRef = useRef<HTMLDivElement>(null);
  const [isAccommodationWarm, setIsAccommodationWarm] = useState(false);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const container = bar.parentElement;
    if (!container) return;

    let rafId: number;

    const update = () => {
      const { top } = container.getBoundingClientRect();
      const show = top <= BAR_HEIGHT;
      bar.style.clipPath = show ? 'inset(0 0 0 0)' : 'inset(0 0 100% 0)';
      bar.style.pointerEvents = show ? 'auto' : 'none';
    };

    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    update();

    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const t = useTranslations('journey');
  const locale = useLocale();

  const tForms = useTranslations('forms');

  const {
    tripId,
    overviewData,
    dateRange,
    travellerCount,
    adultCount,
    childAges,
    rooms,
    isReadOnly,
    datesConfirmed,
    isLiveChecking
  } = useTripOverviewSummary();
  const { totalPrice, isPriceLoading } = useTripPriceSummary();
  const title = overviewData?.journeyTitle ?? '';

  const { travellerCount: selectedTravellerCount, rooms: selectedRooms } = useTripBookingSelection(
    travellerCount,
    rooms
  );
  const isTripUpdating = useIsTripUpdating();
  const pricePerPerson = totalPrice !== undefined ? pricePerPersonForPax(totalPrice, adultCount, childAges) : undefined;

  const travellerSummary = t('sticky_footer_traveller_count', { count: selectedTravellerCount });
  const metaLine = dateRange ? `${dateRange} · ${travellerSummary}` : travellerSummary;

  return (
    <div ref={barRef} className={styles.bar}>
      <div className={styles.inner}>
        <div className={styles.topRow}>
          <p className={styles.title}>{title}</p>
          <div className={styles.topActions}>
            <ShareTripButton className={styles.iconButton} size="large" />
            <SaveTripButton tripId={tripId} className={styles.iconButton} size="large" />
          </div>
        </div>

        <div className={styles.bottomRow}>
          <div className={styles.tabsSlot}>
            <OverviewTabNav items={overviewTabs} offsetPx={SECTION_ACTIVATION_OFFSET} />
          </div>

          <div className={styles.bookingActions}>
            <div className={styles.tripMeta}>
              <span className={styles.metaLine}>{metaLine}</span>
              {pricePerPerson === undefined && isPriceLoading && <TripPriceSkeleton />}
              {pricePerPerson !== undefined && (
                <DialogTrigger>
                  <RACButton className={styles.priceButton} aria-label={t('price_details_aria_label')}>
                    <TripLoyaltyPriceRow>
                      <span className={styles.price}>
                        <AnimatedPrice amountGBP={pricePerPerson} />
                      </span>
                    </TripLoyaltyPriceRow>
                    <span
                      className={styles.priceSuffix}
                    >{` / ${totalPerPersonLabel.split('/')[1]?.trim() ?? 'person'}`}</span>
                  </RACButton>
                  <Popover placement="bottom end" isDismissable hasArrow ariaLabel={totalPerPersonLabel} maxWidth={380}>
                    <TripPriceDetailsContent
                      paxCount={travellerCount}
                      roomCount={selectedRooms}
                      pricePerPerson={pricePerPerson}
                    />
                  </Popover>
                </DialogTrigger>
              )}
            </div>

            <div className={styles.cta}>
              {!isReadOnly && !datesConfirmed ? (
                <Button
                  variant="primary"
                  size="small"
                  isDisabled={isTripUpdating || isLiveChecking}
                  onPress={() => {
                    trackFunnelEvent('trip_funnel_confirm_dates_prompt', { location: 'sticky_bar' });
                    const datesAnchor = globalThis.document.getElementById('overview-booking-dates');
                    if (datesAnchor) void scrollIntoViewSettled(datesAnchor);
                  }}
                >
                  {tForms('confirm_dates')}
                </Button>
              ) : (
                <Link
                  asButton="primary"
                  size="small"
                  href={getPathname({ href: `/journey/accommodation/${tripId}`, locale })}
                  isDisabled={isTripUpdating || isLiveChecking}
                  onClick={() => trackFunnelEvent('trip_funnel_next_accommodation', { location: 'sticky_bar' })}
                  prefetch={isAccommodationWarm && !isTripUpdating ? true : undefined}
                  onPointerEnter={() => setIsAccommodationWarm(true)}
                  onFocus={() => setIsAccommodationWarm(true)}
                >
                  {nextAccommodationLabel} <ArrowRight decorative isInline size="small" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
